import React, { useContext, useMemo } from 'react';
import { createContext } from 'react';
import type { SkillsContextType } from './types';
import { useUserJobsIndex } from '@/data/account/user-jobs';
import { getAllOccupationSkills } from '@/data/account/user-jobs/utils/get-all-occupation-skills';
import { useShowUserFeedback } from '@/data/guidance/feedback';
import { useUserPrioritySkillSetsIndex } from '@/data/taxonomy/user-priority-skill-sets';
import { getManuallyAddedSkills } from '@/packages/skill-management/utils/get-manually-added-skills';
import { markSkillsAsChecked } from '@/packages/skill-management/utils/mark-skills-as-checked';
import { normalizeUserJobsSkills } from '@/packages/skill-management/utils/normalize-user-jobs-skills';

// eslint-disable-next-line react-refresh/only-export-components
export const SkillsContext = createContext<SkillsContextType>({
  userFeedback: undefined,
  jobs: undefined,
  feedbackSkills: [],
  userOccupationSkills: [],
  manuallyAddedSkills: [],
  selectedSkills: [],
  isLoadingSelectedSkills: false,
  isLoadingUserFeedback: false,
  isLoadingJobs: false,
  refetchSelectedSkills: undefined,
});

export const SkillsProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    selectedSkills,
    isLoading: isLoadingSelectedSkills,
    refetch: refetchSelectedSkills,
  } = useUserPrioritySkillSetsIndex();
  const { userFeedback, isLoading: isLoadingUserFeedback } =
    useShowUserFeedback();
  const {
    jobs,
    isLoading: isLoadingJobs,
    refetch: refetchJobs,
  } = useUserJobsIndex();

  const feedbackSkills = useMemo(() => {
    if (!userFeedback?.skills_designations) return [];
    return markSkillsAsChecked(
      userFeedback.skills_designations,
      selectedSkills
    );
  }, [userFeedback, selectedSkills]);

  const userOccupationSkills = useMemo(() => {
    const skills = getAllOccupationSkills(jobs);
    const normalized = normalizeUserJobsSkills(skills);
    return markSkillsAsChecked(normalized, selectedSkills);
  }, [jobs, selectedSkills]);

  const markedSelectedSkills = useMemo(() => {
    return markSkillsAsChecked(selectedSkills);
  }, [selectedSkills]);

  const manuallyAddedSkills = useMemo(
    () =>
      getManuallyAddedSkills(
        markedSelectedSkills,
        feedbackSkills,
        userOccupationSkills
      ),
    [markedSelectedSkills, feedbackSkills, userOccupationSkills]
  );

  const value = useMemo(
    () => ({
      userFeedback,
      feedbackSkills,
      jobs,
      userOccupationSkills,
      manuallyAddedSkills,
      selectedSkills: markedSelectedSkills,
      isLoadingSelectedSkills,
      isLoadingUserFeedback,
      isLoadingJobs,
      refetchSelectedSkills,
      refetchJobs,
    }),
    [
      userFeedback,
      feedbackSkills,
      jobs,
      userOccupationSkills,
      manuallyAddedSkills,
      markedSelectedSkills,
      isLoadingSelectedSkills,
      isLoadingUserFeedback,
      isLoadingJobs,
      refetchSelectedSkills,
      refetchJobs,
    ]
  );

  return (
    <SkillsContext.Provider value={value}>{children}</SkillsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSkills = () => {
  const context = useContext(SkillsContext);
  if (!context) throw new Error('useSkills must be used within SkillsProvider');
  return context;
};
